import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Crown, Users, ArrowLeft, Ban, RotateCcw, CalendarClock, Search, Plus, X } from "lucide-react";

const statusOf = (u) => {
  if (u.is_member) return "active";
  if (u.membership_expires_at) return "expired";
  return "none";
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};

function StatusBadge({ status }) {
  if (status === "active")
    return <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase rounded-full px-2.5 py-1 bg-[#E6B31E] text-black" data-testid="badge-active"><Crown size={12} strokeWidth={3} /> Active</span>;
  if (status === "expired")
    return <span className="text-[10px] font-extrabold uppercase rounded-full px-2.5 py-1 bg-orange-200 text-orange-800" data-testid="badge-expired">Expired</span>;
  return <span className="text-[10px] font-extrabold uppercase rounded-full px-2.5 py-1 bg-black/10 text-black/50" data-testid="badge-none">Never</span>;
}

export default function AdminMembers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (e) { toast.error("Failed to load members"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBan = async (u) => {
    const action = u.is_banned ? "unban" : "ban";
    try {
      await api.post(`/admin/users/${u.user_id}/${action}`);
      toast.success(u.is_banned ? "User unbanned" : "User banned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const grant = async (u, months = 1) => {
    try {
      await api.post("/admin/membership/grant", { user_id: u.user_id, months });
      toast.success(`Granted ${months} month${months > 1 ? "s" : ""} to ${u.username || u.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Revoke membership for ${u.username || u.name}? They'll need to pay again to trade.`)) return;
    try {
      await api.post("/admin/membership/revoke", { user_id: u.user_id });
      toast.success("Membership revoked");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const counts = useMemo(() => {
    const c = { all: users.length, active: 0, expired: 0, none: 0 };
    users.forEach((u) => { c[statusOf(u)]++; });
    return c;
  }, [users]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return users.filter((u) => {
      if (tab !== "all" && statusOf(u) !== tab) return false;
      return !s || (u.username || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
    });
  }, [users, q, tab]);

  const tabs = [
    { key: "all", label: "All users", n: counts.all },
    { key: "active", label: "Active members", n: counts.active },
    { key: "expired", label: "Expired", n: counts.expired },
    { key: "none", label: "Never subscribed", n: counts.none },
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <Link to="/admin" data-testid="back-to-admin" className="inline-flex items-center gap-1.5 font-bold text-sm text-black/50 hover:text-black mb-4">
        <ArrowLeft size={16} strokeWidth={3} /> Back to House dashboard
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <Users className="text-[#B8860B]" size={28} strokeWidth={2.5} />
        <h1 className="font-display font-black text-4xl tracking-tighter">Members & Users</h1>
      </div>
      <p className="text-black/50 font-medium mb-8">Everyone on the platform and their 7 USDT/month membership status.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`text-left rounded-2xl p-5 border-2 border-black nb-shadow transition-colors ${tab === t.key ? "bg-[#E6B31E] text-black" : "bg-white hover:bg-[#FBF7E9]"}`}>
            <div className="text-xs font-bold uppercase opacity-70">{t.label}</div>
            <div className="font-display font-black text-3xl mt-1">{t.n}</div>
          </button>
        ))}
      </div>

      <div className="bg-white nb-border rounded-2xl nb-shadow overflow-hidden">
        <div className="p-4 border-b-2 border-black/5 flex items-center gap-2">
          <Search size={18} className="text-black/40" />
          <input data-testid="member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by username, name or email…"
            className="w-full font-medium outline-none bg-transparent" />
        </div>

        {loading ? (
          <div className="p-10 text-center font-display font-extrabold text-lg animate-pulse">Loading members…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-black/40 font-semibold">No users match this filter.</div>
        ) : (
          <div className="divide-y-2 divide-black/5 max-h-[620px] overflow-y-auto">
            {filtered.map((u) => {
              const st = statusOf(u);
              return (
                <div key={u.user_id} data-testid={`member-row-${u.user_id}`} className="p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold truncate flex items-center gap-2">
                      {u.username || u.name}
                      {u.is_banned && <span className="text-[10px] font-extrabold uppercase rounded-full px-2 py-0.5 border-2 border-black bg-red-200 text-red-800">Banned</span>}
                    </div>
                    <div className="text-xs text-black/50 font-medium truncate">{u.email || "—"} · joined {fmt(u.created_at)}</div>
                  </div>

                  <div className="hidden sm:flex flex-col items-end gap-1 mr-2 min-w-[130px]">
                    <StatusBadge status={st} />
                    {u.membership_expires_at && (
                      <span className={`text-[11px] font-bold flex items-center gap-1 ${st === "active" ? "text-black/50" : "text-orange-700"}`}>
                        <CalendarClock size={12} /> {st === "active" ? "renews" : "expired"} {fmt(u.membership_expires_at)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button data-testid={`grant-btn-${u.user_id}`} onClick={() => grant(u, 1)}
                      className="flex items-center gap-1 font-bold text-sm rounded-full px-3 py-2 border-2 border-black bg-[#E6B31E] text-black hover:bg-[#f5c841]" title="Grant / extend 1 month">
                      <Plus size={14} strokeWidth={3} /> Month
                    </button>
                    {st !== "none" && (
                      <button data-testid={`revoke-btn-${u.user_id}`} onClick={() => revoke(u)}
                        className="flex items-center gap-1 font-bold text-sm rounded-full px-3 py-2 border-2 border-black bg-white text-black hover:bg-black/5" title="Revoke membership">
                        <X size={14} strokeWidth={3} /> Revoke
                      </button>
                    )}
                    <button data-testid={`ban-btn-${u.user_id}`} onClick={() => toggleBan(u)}
                      className={`flex items-center gap-1 font-bold text-sm rounded-full px-3 py-2 border-2 border-black ${u.is_banned ? "bg-white text-black" : "bg-red-500 text-white"}`}>
                      {u.is_banned ? <><RotateCcw size={14} strokeWidth={3} /> Unban</> : <><Ban size={14} strokeWidth={3} /> Ban</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
